import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signUp } from 'aws-amplify/auth';
import './shared/AuthStyles.css';
import './MantineSignUpPage.css';

import {
  TextInput,
  Button,
  Text,
  Paper,
  Container,
  Stack,
  Group,
} from '@mantine/core';
import PasswordRequirementInput from './common/PasswordRequirementInput';
import { isPasswordValid } from '../utils/passwordValidation';
function MantineSignUpPage({ onPageChange }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (!isPasswordValid(formData.password)) {
      newErrors.password = 'Password is missing required characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const cognitoResponse = await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            given_name: formData.firstName,
            family_name: formData.lastName,
            "custom:role": "Owner"
          },
          autoSignIn: {
            enabled: true,
            authFlowType: "USER_AUTH"
          }
        }
      });

      if (cognitoResponse.nextStep.signUpStep !== "CONFIRM_SIGN_UP") {
        throw new Error('Failed to create user');
      } else {
        navigate('/verify-email', { 
          state: { username: formData.email }
        });
      }
    } catch (error) {
      console.error('Error signing up:', error);
      setErrors(prev => ({
        ...prev,
        submit: error.message
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="auth-container">
        <div className="auth-tabs">
          <button 
            className="auth-tab active"
            onClick={() => onPageChange('signup')}
          >
            Sign up
          </button>
          <button 
            className="auth-tab"
            onClick={() => onPageChange('login')}
          >
            Log in
          </button>
        </div>
      </div>
      <Container size="xs" mt="xl" py="md">
        <Paper py="xl">
          {errors.submit && <Text size="md" ta="center" mb="xl" c="red">{errors.submit}</Text>}
          <form onSubmit={handleSubmit}>
            <Stack>
              <Group grow>
                <TextInput
                  label="First name"
                  placeholder="Enter first name"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  error={errors.firstName}
                  size="md"
                  radius="md"
                  required
                />
                <TextInput
                  label="Last name"
                  placeholder="Enter last name"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  error={errors.lastName}
                  size="md"
                  radius="md"
                  required
                />
              </Group>

              <TextInput
                label="Email address"
                placeholder="Enter email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={errors.email}
                size="md"
                radius="md"
                required
              />

              <PasswordRequirementInput
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                error={errors.password}
                size="md"
                radius="md"
                required
                showRequirements={true}
              />

              <Button 
                type="submit"
                loading={isLoading}
                fullWidth
                size="md"
                radius="xl"
                variant="filled"
                mt="1rem"
              >
                Sign Up
              </Button>
            </Stack>
          </form>
        </Paper>
      </Container>
    </div>
  );
}

export default MantineSignUpPage; 